// src/components/Admin/BulkEditDialog.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Button, TextField, MenuItem, Chip, Stack, Alert, Typography, Switch, FormControlLabel
} from "@mui/material";
import axios from "axios";

function uniqInts(list) {
    return Array.from(new Set((list || []).map(n => Number(n)).filter(n => Number.isInteger(n))));
}

function parseCodesText(txt) {
    if (!txt) return [];
    return txt
        .split(/[,\s]+/)
        .map(s => s.trim())
        .filter(Boolean)
        .map(Number)
        .filter(n => Number.isInteger(n));
}

/**
 * Recibe:
 *  - open: bool
 *  - onClose(): fn
 *  - selectedCodes: number[] (de la tabla)
 *  - onApplied(): fn -> para refrescar la tabla tras guardar
 */
export default function BulkEditDialog({ open, onClose, selectedCodes = [], onApplied }) {
    // Normaliza lo que llegue (undefined, Set, Map, iterable, número, etc.) a array de números
    const toArrayish = (x) => {
        if (!x) return [];
        if (Array.isArray(x)) return x;
        if (typeof x.size === "number" && typeof x.forEach === "function") {
            const out = [];
            x.forEach((v) => out.push(v));
            return out;
        }
        try { return Array.from(x); } catch { /* noop */ }
        return [x];
    };
    const selectedCodesSafe = toArrayish(selectedCodes);
    const API = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("token");
    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    // catálogos
    const [estados, setEstados] = useState([]);
    const [lugares, setLugares] = useState([]);
    const [ambitos, setAmbitos] = useState([]);
    const [especialidades, setEspecialidades] = useState([]);

    // códigos a aplicar
    const [codes, setCodes] = useState(uniqInts(selectedCodesSafe));
    const [codesInput, setCodesInput] = useState("");

    // valores del formulario (null = no tocar)
    const [estadoId, setEstadoId] = useState(null);
    const [lugaresIds, setLugaresIds] = useState(null);
    const [ambitosIds, setAmbitosIds] = useState(null);
    const [especialidadesIds, setEspecialidadesIds] = useState(null);
    // moroso: null (sin tocar) | "1" | "0"
    const [moroso, setMoroso] = useState(null);
    const morosoChecked = moroso === "1";

    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [okMsg, setOkMsg] = useState("");

    // resetea al abrir/cerrar
    useEffect(() => {
        if (open) {
            console.log("[BulkEditDialog] selectedCodes raw =", selectedCodes);
            setCodes(uniqInts(selectedCodesSafe));
            setCodesInput("");
            setEstadoId(null);
            setLugaresIds(null);
            setAmbitosIds(null);
            setEspecialidadesIds(null);
            setMoroso(null);
            setErrorMsg("");
            setOkMsg("");
        }
    }, [open, selectedCodesSafe]);

    // carga catálogos
    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const [resE, resL, resA, resESP] = await Promise.all([
                    axios.get(`${API}/api/estados`, { headers }),
                    axios.get(`${API}/api/lugares-trabajo`, { headers }),
                    axios.get(`${API}/api/ambitos-trabajo`, { headers }),
                    axios.get(`${API}/api/especialidades`, { headers }),
                ]);
                const toArr = d => Array.isArray(d?.data?.content) ? d.data.content : Array.isArray(d?.data) ? d.data : [];
                setEstados(toArr(resE));
                setLugares(toArr(resL));
                setAmbitos(toArr(resA));
                setEspecialidades(toArr(resESP));
            } catch (e) {
                setErrorMsg("No se pudieron cargar catálogos.");
                console.error(e);
            }
        })();
    }, [open, API, headers]);

    // añadir más códigos manualmente
    const handleAddCodes = () => {
        const parsed = parseCodesText(codesInput);
        if (!parsed.length) return;
        setCodes(uniqInts([...codes, ...parsed]));
        setCodesInput("");
    };

    // quitar un código individual
    const removeCode = (c) => {
        setCodes(prev => prev.filter(x => x !== c));
    };

    const buildPayload = () => {
        const payload = { codigos: codes };
        if (estadoId !== null) payload.estadoId = Number(estadoId);
        if (lugaresIds !== null) payload.lugaresTrabajoIds = lugaresIds.map(Number);
        if (ambitosIds !== null) payload.ambitosTrabajoIds = ambitosIds.map(Number);
        if (especialidadesIds !== null) payload.especialidadesIds = especialidadesIds.map(Number);
        if (moroso !== null) payload.moroso = moroso; // "1" | "0"
        return payload;
    };

    const handleSubmit = async () => {
        setErrorMsg("");
        setOkMsg("");

        if (!codes.length) {
            setErrorMsg("Añade al menos un código de colegiado.");
            return;
        }
        // si no hay ningún campo a modificar, no tiene sentido llamar
        if (
            estadoId === null &&
            lugaresIds === null &&
            ambitosIds === null &&
            especialidadesIds === null &&
            moroso === null
        ) {
            setErrorMsg("Selecciona al menos un campo para actualizar.");
            return;
        }

        try {
            setLoading(true);
            const body = buildPayload();
            const res = await axios.put(`${API}/api/admin/colegiados/bulk-update`, body, { headers });
            const { actualizados, no_encontrados, errores } = res.data || {};
            setOkMsg(`Actualizados: ${actualizados || 0}${no_encontrados?.length ? ` · No encontrados: ${no_encontrados.length}` : ""}${(errores && Object.keys(errores).length) ? ` · Errores: ${Object.keys(errores).length}` : ""}`);
            if (typeof onApplied === "function") onApplied();
        } catch (e) {
            console.error(e);
            const raw = e?.response?.data ?? e?.message ?? e;
            setErrorMsg(typeof raw === "string" ? raw : (raw?.message || "No se pudo aplicar la edición en grupo"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Edición en grupo</DialogTitle>
            <DialogContent dividers>

                {/* Códigos seleccionados */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Códigos de colegiado ({codes.length})
                    </Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {codes.map(c => (
                            <Chip key={c} label={c} onDelete={() => removeCode(c)} />
                        ))}
                        {!codes.length && <Typography variant="body2" color="text.secondary">No hay códigos aún.</Typography>}
                    </Stack>

                    <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Añadir más códigos (separados por coma o espacio)"
                            placeholder="101, 102, 205"
                            value={codesInput}
                            onChange={(e) => setCodesInput(e.target.value)}
                        />
                        <Button variant="outlined" onClick={handleAddCodes}>Añadir</Button>
                    </Box>
                </Box>

                {/* Campos a actualizar */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: 2,
                    }}
                >
                    {/* Estado */}
                    <TextField
                        select
                        label="Estado (opcional)"
                        value={estadoId === null ? "" : String(estadoId)}
                        onChange={(e) => setEstadoId(e.target.value === "" ? null : Number(e.target.value))}
                        helperText="Déjalo vacío para no modificar."
                    >
                        <MenuItem value="">(no modificar)</MenuItem>
                        {estados.map(e => (
                            <MenuItem key={e.id} value={String(e.id)}>{e.nombre}</MenuItem>
                        ))}
                    </TextField>

                    {/* Moroso */}
                    <Box>
                        <Typography variant="caption" color="text.secondary">Moroso</Typography>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Button
                                size="small"
                                variant={moroso === null ? "contained" : "outlined"}
                                onClick={() => setMoroso(null)}
                            >
                                No modificar
                            </Button>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={morosoChecked}
                                        onChange={(_, checked) => setMoroso(checked ? "1" : "0")}
                                    />
                                }
                                label={morosoChecked ? "Sí" : "No"}
                            />
                        </Stack>
                    </Box>

                    {/* Lugares */}
                    <TextField
                        select
                        SelectProps={{ multiple: true, renderValue: (sel) => sel.length ? `${sel.length} seleccionados` : "(no modificar)" }}
                        label="Lugares de trabajo"
                        value={lugaresIds === null ? [] : lugaresIds.map(String)}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val.length === 0) setLugaresIds(null);
                            else setLugaresIds(val.map(Number));
                        }}
                        helperText="Vacío = no modificar. Si eliges alguno, reemplaza por completo."
                    >
                        <MenuItem value="">(no modificar)</MenuItem>
                        {lugares.map(l => (
                            <MenuItem key={l.id} value={String(l.id)}>{l.nombre}</MenuItem>
                        ))}
                    </TextField>

                    {/* Ámbitos */}
                    <TextField
                        select
                        SelectProps={{ multiple: true, renderValue: (sel) => sel.length ? `${sel.length} seleccionados` : "(no modificar)" }}
                        label="Ámbitos de trabajo"
                        value={ambitosIds === null ? [] : ambitosIds.map(String)}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val.length === 0) setAmbitosIds(null);
                            else setAmbitosIds(val.map(Number));
                        }}
                        helperText="Vacío = no modificar. Si eliges alguno, reemplaza por completo."
                    >
                        <MenuItem value="">(no modificar)</MenuItem>
                        {ambitos.map(a => (
                            <MenuItem key={a.id} value={String(a.id)}>{a.nombre}</MenuItem>
                        ))}
                    </TextField>

                    {/* Especialidades */}
                    <TextField
                        select
                        SelectProps={{ multiple: true, renderValue: (sel) => sel.length ? `${sel.length} seleccionadas` : "(no modificar)" }}
                        label="Especialidades"
                        value={especialidadesIds === null ? [] : especialidadesIds.map(String)}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val.length === 0) setEspecialidadesIds(null);
                            else setEspecialidadesIds(val.map(Number));
                        }}
                        helperText="Vacío = no modificar. Si eliges alguna, reemplaza por completo."
                    >
                        <MenuItem value="">(no modificar)</MenuItem>
                        {especialidades.map(esp => (
                            <MenuItem key={esp.id} value={String(esp.id)}>{esp.nombre}</MenuItem>
                        ))}
                    </TextField>
                </Box>

                {errorMsg && <Alert severity="error" sx={{ mt: 2 }}>{errorMsg}</Alert>}
                {okMsg && <Alert severity="success" sx={{ mt: 2 }}>{okMsg}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Cerrar</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={loading}>
                    Aplicar cambios
                </Button>
            </DialogActions>
        </Dialog>
    );
}
